import {weakMemo} from '@packages/utilities/memo';
import {Qualified, type PluginName} from '@packages/systems/wfdl-Qualified';
import * as StyleVariables from '@packages/systems/core/utils/StyleVariables';
import {BreakpointsConfig, StyleBlockId} from '@packages/systems/style/types';
import {StyleBlock} from '@packages/systems/style-legacy/records';
import {Lazy} from '@packages/utilities/lazy';

export const NO_PSEUDO = 'noPseudo';
export const ODD_CHILD = 'nth-child(odd)';
export const EVEN_CHILD = 'nth-child(even)';
export const FIRST_CHILD = 'first-child';
export const LAST_CHILD = 'last-child';
export const DISABLED = 'disabled';
export const HOVER = 'hover';
export const ACTIVE = 'active';
export const VISITED = 'visited';
export const FOCUS = 'focus';
export const FOCUS_VISIBLE = 'focus-visible';
export const FOCUS_WITHIN = 'focus-within';
export const PLACEHOLDER = 'placeholder';
export const EMPTY = 'empty';
export const BEFORE = 'before';
export const AFTER = 'after';
export const BACKDROP = 'backdrop';
export const LANG = 'lang';
export const POPOVER_OPEN = 'popover-open';
export const DATA = 'data';

export const pseudoMap = {
  [NO_PSEUDO]: NO_PSEUDO,
  [ODD_CHILD]: ODD_CHILD,
  [EVEN_CHILD]: EVEN_CHILD,
  [FIRST_CHILD]: FIRST_CHILD,
  [LAST_CHILD]: LAST_CHILD,
  [DISABLED]: DISABLED,
  [HOVER]: HOVER,
  [ACTIVE]: ACTIVE,
  [VISITED]: VISITED,
  [FOCUS]: FOCUS,
  [FOCUS_VISIBLE]: FOCUS_VISIBLE,
  [FOCUS_WITHIN]: FOCUS_WITHIN,
  [PLACEHOLDER]: PLACEHOLDER,
  [EMPTY]: EMPTY,
  [BEFORE]: BEFORE,
  [AFTER]: AFTER,
  [BACKDROP]: BACKDROP,
  [LANG]: LANG,
  [POPOVER_OPEN]: POPOVER_OPEN,
  [DATA]: DATA,
} as const;

export type PseudoStateKey =
  | keyof typeof pseudoMap
  | `data-${string}`
  | `lang(${string})`;

export const normalizePseudoState = (pseudoState: string): string => {
  return (
    pseudoState
      // The pseudoState can be a string like "lang(en)" or "lang(en-us)", and we want to normalize it to "lang" so lookups are easier.
      .replace(/^lang\([A-Za-z0-9-_]+\)/, 'lang')
      // It can also be a string like "data-foo" or "data-foo-bar", which we want to normalize to "data" for the same reason.
      .replace(/^data-[a-z]+(?:-[a-z0-9]+)*$/i, 'data')
  );
};

const VALID_PSEUDO_STATE_KEYS = new Set(Object.keys(pseudoMap));
export const getPseudoStateKey = (str: string): null | PseudoStateKey => {
  if (VALID_PSEUDO_STATE_KEYS.has(normalizePseudoState(str))) {
    return str as PseudoStateKey;
  }
  return null;
};

export type CustomNodeStateKey =
  | 'open'
  | 'current'
  | 'redirected-checked'
  | 'redirected-focus'
  | 'redirected-focus-visible'
  | 'ecommerce-pill-selected'
  | 'ecommerce-pill-disabled'
  | 'ecommerce-add-to-cart-disabled'
  | 'ecommerce-buy-now-disabled';

export type CombinatorKey = '' | '|' | '&';

export type BlockType = 'tag' | 'class' | 'element';

// TODO enum the migration keys
type Migration = string;

export type SiteStyleData = {
  breakpoints: BreakpointsConfig;
  migrations: Record<Migration, boolean>;
  swatches: SwatchType[];
  macros?: null | RawMacroServerType[];
  appliedStylesMap: StylesMap;
};

export type SiteStyle = {
  blocks: Array<RawStyleBlockServerType & {macro?: boolean}>;
  data: SiteStyleData;
};

export type BaseSiteStyle = SiteStyle;

export type RawStyleBlockServerDataType = {
  type: BlockType;
  name: string;
  createdBy?: string | null;
  namespace?: string | null;
  sel?: string;
  comb?: CombinatorKey;
  styleLess?: string;
  // DEPRECATED
  // Available only on sites which didn't sync styles from the designer since this notice.
  // Property `styleText` was our primary syncing mechanism and key in the first 2 years of Webflows history.
  // It and `styleLess` were duplicated and incompatible sources of truth for a long period, which we have code
  // to migrate and patch between. Now we finally don't need to send both properties to and from the server.
  // This keyword has been adapted outside of style blocks and should be migrated to styleLess as well in the long run.
  styleText?: string;
  variants?: {
    [name: string]: StyleBlockVariantType;
  };
  children?: string[];
  affects?: {
    [key: string]: number;
  };
  migrations?: Partial<Record<Migration, true>>;
  pluginType?: PluginName;
  origin?: Qualified<StyleBlockId>;
};

export type StyleBlockVariantType = {
  sel?: RawStyleBlockServerDataType['sel'];
  styleLess?: RawStyleBlockServerDataType['styleLess'];
  styleText?: RawStyleBlockServerDataType['styleText'];
};

// DEPRECATED - These are legacy style blocks from IX1 macros
export type RawMacroStyleBlockServerType = {
  _id: string;
  macro: boolean;
  data: {
    sel?: RawStyleBlockServerDataType['sel'];
    styleText?: RawStyleBlockServerDataType['styleText'];
  };
};

// DEPRECATED - These are IX1 macros, the raw server version of IXMacroShape
export type RawMacroServerType = {
  _id: string | null;
  slug: string | null;
  name: string | null;
  value: Record<string, unknown>;
};

export type RawStyleBlockServerType = {
  _id: string;
  fake?: boolean;
  data: RawStyleBlockServerDataType;
};

export type SwatchType = {
  id: string;
  previewColor: string;
  color: string;
  name: string;
};

export type GlobalSwatchReference = {
  type: 'global-swatch-reference';
  id: string;
};

export type ColorPrimitive = Readonly<{
  type: 'color';
  value: string;
}>;

export type ColorValue =
  | ColorPrimitive
  | GlobalSwatchReference
  | StyleVariables.VariableReference;

export type StylesMap = {
  [key: string]: RawStyleBlockServerType | null | undefined;
};

/**
 * Type representing string values of all supported unit types that can apply to
 * length values. May not include all length units supported by all major
 * browsers.
 *
 * Learn more here:
 * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/length
 */
export type LengthUnit =
  | 'px'
  | 'em'
  | 'rem'
  | 'vh'
  | 'vw'
  | 'dvh'
  | 'dvw'
  | 'lvh'
  | 'lvw'
  | 'svh'
  | 'svw'
  | 'vmax'
  | 'vmin'
  | 'ch';

/**
 * All properties that come with a value and unit
 */
export type LengthValue = UnitValue<LengthUnit>;
export type NumberValue = UnitValue<'-'>;
export type PercentValue = UnitValue<'%'>;

export type UnitValue<U extends string> = {
  value: number;
  unit: U;
  type?: void;
};

export const SYNC_STATUS = Object.freeze({
  IN_SYNC: 'IN_SYNC',
  OUT_OF_SYNC: 'OUT_OF_SYNC',
  SYNCING: 'SYNCING',
});

/**
 * Determines whether/which variables and variableCollections need to be synced to the backend
 *
 * syncRetryCount is to avoid infinite retries. It's marked optional and defaulted to 0 in order
 * to maintain backward compatibility with previous usage of CssVariablesStore.
 */
export type CssVariablesStateType = {
  swatches: SwatchType[];
  variables: StyleVariables.VariableMap;
  variableCollections: StyleVariables.VariableCollectionMap;
  sync: (typeof SYNC_STATUS)[keyof typeof SYNC_STATUS];
  ephemeralVariables: null | StyleVariables.AnyVariableType[];
  syncRetryCount?: number;
};

/**
 * Get a "live" view of all variables, where ephemeral changes are part of the
 * returned `VariableMap`.
 *
 * This should only need to be used in places where live changes to variables
 * need to be reflected (primarily things related to the Designer canvas).
 */
export const getLiveVariables = weakMemo(
  (state: CssVariablesStateType): StyleVariables.VariableMap => {
    if (state.ephemeralVariables) {
      return StyleVariables.upsertMultipleVariables(
        state.variables,
        state.ephemeralVariables
      );
    }

    return state.variables;
  }
);

/**
 * Checks if the value is a valid unit value.
 * Example usage found in `GridGapSlider`
 */
export const isValidUnitValue = <T extends string>(
  value: UnitValue<T> | StyleVariables.VariableReference
): value is UnitValue<T> => typeof value.value === 'number';

/**
 * A special custom css property name format which is used to indicate that
 * we're applying a mixin.
 */
export type MixinKey = `---${string}`;

/**
 * A nested map of property-value pairs, each representing a unique "mixin"
 * (reusable set of CSS properties and values, prefixed with '---') to
 * replace the property-value pair when rendering styles.
 *
 * Used in the CSS renderer pipeline.
 *
 * For instance, suppose we had the following `styleBlock` & `mixinMap`:
 *
 * ```javascript
 * const styleBlock = {
 *   _id: 'id-1234',
 *   type: 'class',
 *   name: 'some-class',
 *   styleLess: 'font-family: Helvetica; ---mixin: foo; ---other-mixin: bar;'
 * }
 *
 * const mixinMap = {
 *   '---mixin': {
 *     foo: 'color: red; background-color: green;'
 *   },
 *   '---other-mixin: {
 *     bar: 'border: 4px solid orange; box-shadow: 12px 12px 2px 1px black;'
 *   }
 * }
 * ```
 *
 * The renderer pipeline will output the following CSS:
 *
 * ```css
 * .some-class {
 *   font-family: Helvetica;
 *
 *   // ---mixin: foo;
 *   color: red;
 *   background-color: green;
 *
 *   // ---other-mixin: bar;
 *   border: 4px solid orange;
 *   box-shadow: 12px 12px 2px 1px black;
 * }
 * ```
 */
export type MixinMap = {
  [k: MixinKey]: {
    [v: string]: Lazy<StyleBlock['styleLess']>;
  };
};

/**
 * A specially-formatted string which encodes both the `MixinKey` and the mixin
 * value string, separated by `:`.
 *
 * Used mainly in `StyleSheetUtils` & friends to signify which mixins are
 * applied to a given `WebflowCSSRule`.
 */
export type AppliedMixinKey = `${MixinKey}:${string}`;
